import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import InvoicePreview from './InvoicePreview';
import { useCustomDialog } from '../../context/DialogContext';
import useWindowFocus from '../../hooks/useWindowFocus';

// ─── Mobile Detection Hook ───────────────────────────────────────────────────
const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return isMobile;
};

const POS = ({ token, isFreeTier }) => {
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    const isMobile = useIsMobile();
    const [menu, setMenu] = useState([]);
    const [categories, setCategories] = useState([]);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    
    // Cart & Selection
    const [cart, setCart] = useState([]);
    const [tableNo, setTableNo] = useState('');
    const [orderType, setOrderType] = useState('Dine-in');
    const [custName, setCustName] = useState('');
    const [custPhone, setCustPhone] = useState('');
    const [custAddr, setCustAddr] = useState('');
    const [paymentMode, setPaymentMode] = useState('Cash');
    const [applyServiceCharge, setApplyServiceCharge] = useState(true);
    const [applyDeliveryFee, setApplyDeliveryFee] = useState(true);

    const [activeCategory, setActiveCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [discountVal, setDiscountVal] = useState(0); 
    const [discountType, setDiscountType] = useState('Fixed'); // 'Fixed' or 'Percent'
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [activeTab, setActiveTab] = useState(isFreeTier ? 'history' : 'billing'); // 'billing' or 'history'
    
    const [history, setHistory] = useState([]);
    const [hFilters, setHFilters] = useState({ status: '', order_type: '', payment_mode: '', search: '' });
    const [selectedOrder, setSelectedOrder] = useState(null); // ID for invoice preview
    const [settleOrder, setSettleOrder] = useState(null); // Order object for settlement modal
    const [variantModalItem, setVariantModalItem] = useState(null);
    
    const [msg, setMsg] = useState({ type: '', text: '' });
    const [saving, setSaving] = useState(false);
    
    // UI Collapse States
    const [showCustInfo, setShowCustInfo] = useState(false);
    const [showDiscount, setShowDiscount] = useState(false);

    const fetchHistory = React.useCallback(async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=fetch_orders', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify(hFilters)
            });
            const json = await res.json();
            if (json.success) setHistory(json.orders || []);
        } catch (err) {
            console.error("Fetch History Error:", err);
        }
    }, [token, hFilters]);

    const handleSettleOrder = async (orderId, pMode) => {
        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=settle_order', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ order_id: orderId, payment_mode: pMode })
            });
            const json = await res.json();
            if (json.success) {
                setSettleOrder(null);
                fetchHistory();
                setMsg({ type: 'success', text: 'Order settled successfully!' });
                setTimeout(() => setMsg({ type: '', text: '' }), 3000);
            }
        } catch (err) {
            console.error("Settle Order Error:", err);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteOrder = async (orderId) => {
        if (!await customConfirm("ARE YOU SURE? This will permanently delete the order and revert any associated loyalty points. This action cannot be undone.", "Delete Order", "danger")) return;
        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=delete_order', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ order_id: orderId })
            });
            const json = await res.json();
            if (json.success) {
                fetchHistory();
                setMsg({ type: 'success', text: 'Order permanently deleted.' });
                setTimeout(() => setMsg({ type: '', text: '' }), 3000);
            } else {
                await customAlert(json.message || "Failed to delete order", "Error", "danger");
            }
        } catch (err) {
            console.error("Delete Order Error:", err);
            await customAlert("An error occurred while deleting the order.", "Error", "danger");
        } finally {
            setSaving(false);
        }
    };



    const fetchInitialData = React.useCallback(async () => {
        try {
            // 1. Fetch Menu
            const menuRes = await fetch('https://menu.searchisend.in/apimenu/v2/v2_manage_menu.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const menuJson = await menuRes.json();
            if (menuJson.success) {
                setMenu(menuJson.items);
                const cats = [...new Set(menuJson.items.map(i => i.category))];
                setCategories(cats);
            }

            // 2. Fetch Billing Config
            const configRes = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=get_config', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const configJson = await configRes.json();
            if (configJson.success) setConfig(configJson.config);
            
            fetchHistory();
        } catch (err) {
            console.error("POS Data Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    }, [token, fetchHistory]);

    useEffect(() => {
        fetchInitialData();
        
        // Auto-refresh history every 30 seconds
        const intervalId = setInterval(() => {
            fetchHistory();
        }, 30000);
        
        return () => clearInterval(intervalId);
    }, [fetchInitialData, fetchHistory]);

    // Re-fetch when tab is brought back to focus
    useWindowFocus(() => {
        fetchHistory();
    });

    // --- Cart Actions ---
    const addToCart = (item, selectedSize = null) => {
        // If it has multiple sizes and none selected yet, show modal
        if (!selectedSize && item.sizes && item.sizes.length > 1) {
            setVariantModalItem(item);
            return;
        }

        const cartItem = {
            item_id: item.item_id,
            name: item.name + (selectedSize ? ` (${selectedSize.size_name})` : ''),
            price: selectedSize ? parseFloat(selectedSize.price) : (item.price || item.sizes?.[0]?.price || 0),
            quantity: 1,
            size_id: selectedSize ? selectedSize.size_id : null
        };

        setCart(prev => {
            const existingIndex = prev.findIndex(i => i.item_id === cartItem.item_id && i.name === cartItem.name);
            if (existingIndex > -1) {
                const updated = [...prev];
                updated[existingIndex] = { 
                    ...updated[existingIndex], 
                    quantity: updated[existingIndex].quantity + 1 
                };
                return updated;
            }
            return [...prev, cartItem];
        });
        
        setVariantModalItem(null);
    };

    const updateQty = (id, itemName, delta) => {
        setCart(prev => prev.map(i => {
            if (i.item_id === id && i.name === itemName) {
                const newQty = Math.max(0, i.quantity + delta);
                return { ...i, quantity: newQty };
            }
            return i;
        }).filter(i => i.quantity > 0));
    };

    // --- Calculations ---
    const subTotal = useMemo(() => cart.reduce((sum, i) => sum + (i.price * i.quantity), 0), [cart]);
    
    const discountAmount = useMemo(() => {
        let amt = 0;
        if (appliedCoupon) {
            if (appliedCoupon.discount_type === 'Percentage') {
                amt = (subTotal * (appliedCoupon.discount_value / 100));
            } else {
                amt = appliedCoupon.discount_value;
            }
        } else if (discountVal > 0) {
            if (discountType === 'Percent') {
                amt = (subTotal * (discountVal / 100));
            } else {
                amt = discountVal;
            }
        }
        return Math.min(amt, subTotal);
    }, [subTotal, appliedCoupon, discountVal, discountType]);

    const taxableSubtotal = subTotal - discountAmount;

    const gstAmount = useMemo(() => {
        if (!config || !config.gst_percent) return 0;
        return (taxableSubtotal * ((parseFloat(config.gst_percent) || 0) / 100));
    }, [taxableSubtotal, config]);

    const serviceChargeAmount = useMemo(() => {
        if (!config || !applyServiceCharge || !config.service_charge) return 0;
        return (taxableSubtotal * ((parseFloat(config.service_charge) || 0) / 100));
    }, [taxableSubtotal, config, applyServiceCharge]);

    const taxTotal = gstAmount + serviceChargeAmount;

    const deliveryFee = useMemo(() => {
        if (!config || orderType !== 'Delivery' || String(config.is_delivery_enabled) !== '1' || !applyDeliveryFee) return 0;
        const threshold = parseFloat(config.free_delivery_over) || 0;
        if (threshold > 0 && taxableSubtotal >= threshold) return 0;
        return parseFloat(config.delivery_charge) || 0;
    }, [config, orderType, taxableSubtotal, applyDeliveryFee]);

    const grandTotal = taxableSubtotal + taxTotal + deliveryFee;

    const handleApplyCoupon = async () => {
        if (!couponCode) return;
        setSaving(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_coupons.php?action=validate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ coupon_code: couponCode })
            });
            const json = await res.json();
            if (json.success) {
                if (subTotal < json.coupon.min_order_value) {
                    setMsg({ type: 'error', text: `Min. order for this coupon is ${config?.currency_symbol || '₹'}${json.coupon.min_order_value}` });
                } else {
                    setAppliedCoupon(json.coupon);
                    setDiscountVal(0); // Clear manual discount
                    setMsg({ type: 'success', text: 'Coupon applied!' });
                }
            } else {
                setMsg({ type: 'error', text: json.message });
            }
        } catch (err) {
            console.error("Coupon Error:", err);
        } finally {
            setSaving(false);
            setTimeout(() => setMsg({ type: '', text: '' }), 3000);
        }
    };

    const handleCheckout = async (status = 'Paid') => {
        if (cart.length === 0) {
            await customAlert("Cart is empty!", "Empty Cart", "warning");
            return;
        }
        setSaving(true);
        setMsg({ type: '', text: '' });

        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=create_order', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    table_no: tableNo,
                    order_type: orderType,
                    customer_name: custName,
                    customer_phone: custPhone,
                    customer_address: custAddr,
                    items: cart,
                    sub_total: subTotal,
                    tax_total: taxTotal,
                    discount_total: discountAmount,
                    delivery_fee: deliveryFee,
                    grand_total: grandTotal,
                    payment_mode: paymentMode,
                    order_status: status
                })
            });
            const json = await res.json();
            if (json.success) {
                setMsg({ type: 'success', text: `Order ${json.order_no} created successfully!` });
                setCart([]);
                setTableNo('');
                setCustName('');
                setCustPhone('');
                setCustAddr('');
                setDiscountVal(0);
                setAppliedCoupon(null);
                setCouponCode('');
                fetchHistory();
                if (status === 'Paid') setSelectedOrder(json.order_id);
            } else {
                setMsg({ type: 'error', text: json.message });
            }
        } catch (err) {
            setMsg({ type: 'error', text: 'Checkout failed. Please try again.' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Initializing POS...</div>;
    
    if (!config || !config.currency_symbol) return (
        <div style={{ padding: '60px 20px', textAlign: 'center', minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: '80px', height: '80px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                <i className="fas fa-file-invoice-dollar" style={{ fontSize: '2.5rem', color: '#f59e0b' }} />
            </div>
            <h2 style={{ fontSize: '1.8rem', color: 'var(--text-color)', margin: '0 0 10px 0' }}>Setup Required</h2>
            <p style={{ color: 'var(--text-color-secondary)', fontSize: '1.1rem', maxWidth: '400px', margin: '0 auto 30px', lineHeight: '1.5' }}>
                Before starting the billing, you have to configure the billing settings of your restaurant.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', alignItems: 'center' }}>
                <Link to="/manage_v2_secure_auth/billing-settings" style={{ backgroundColor: 'var(--primary-orange)', color: '#fff', textDecoration: 'none', padding: '12px 24px', borderRadius: '50px', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 14px rgba(234,88,12,0.3)', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(234,88,12,0.4)'; }} onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(234,88,12,0.3)'; }}>
                    <i className="fas fa-cog"></i> Configure Billing Settings
                </Link>
                <Link to="/manage_v2_secure_auth/" style={{ color: 'var(--text-color-secondary)', textDecoration: 'none', fontSize: '0.95rem', fontWeight: '500', transition: 'color 0.2s' }} onMouseEnter={e => e.target.style.color = 'var(--text-color)'} onMouseLeave={e => e.target.style.color = 'var(--text-color-secondary)'}>
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </Link>
            </div>
        </div>
    );

    const filteredItems = menu.filter(i => 
        (activeCategory === 'All' || i.category === activeCategory) &&
        (i.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : 'calc(100vh - 110px)', maxHeight: isMobile ? 'none' : 'calc(100vh - 110px)', gap: '10px', overflow: isMobile ? 'visible' : 'hidden', paddingBottom: '10px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px', flexWrap: 'wrap', gap: '8px' }}>
                <Link to="/manage_v2_secure_auth/" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#64748b', textDecoration: 'none', fontSize: '0.85em', background: '#f1f5f9', padding: '6px 12px', borderRadius: '50px', transition: '0.2s', whiteSpace: 'nowrap' }}>
                    <i className="fas fa-arrow-left" /> Back
                </Link>
                
                {/* Tabs Header */}
                <div style={{ display: 'flex', gap: '5px', background: '#f1f5f9', padding: '4px', borderRadius: '12px' }}>
                    <button 
                        onClick={async () => {
                            if (isFreeTier) {
                                await customAlert("Manual Billing is a Pro feature. Please upgrade to create in-house orders.", "Pro Feature", "info");
                                return;
                            }
                            setActiveTab('billing');
                        }} 
                        style={{ background: activeTab === 'billing' ? '#fff' : 'transparent', border: 'none', fontWeight: '700', padding: '8px 16px', cursor: 'pointer', borderRadius: '10px', color: activeTab === 'billing' ? 'var(--primary-orange)' : '#64748b', fontSize: '0.85em', boxShadow: activeTab === 'billing' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', minHeight: '44px', touchAction: 'manipulation', opacity: isFreeTier ? 0.6 : 1 }}
                    >
                        <i className="fas fa-cash-register" style={{ marginRight: '6px' }} />{isMobile ? '' : 'Billing'}
                        {isMobile ? 'Bill' : ''}
                        {isFreeTier && <i className="fas fa-lock" style={{ marginLeft: '6px', fontSize: '0.8em' }} />}
                    </button>
                    <button 
                        onClick={() => setActiveTab('history')} 
                        style={{ background: activeTab === 'history' ? '#fff' : 'transparent', border: 'none', fontWeight: '700', padding: '8px 16px', cursor: 'pointer', borderRadius: '10px', color: activeTab === 'history' ? 'var(--primary-orange)' : '#64748b', fontSize: '0.85em', boxShadow: activeTab === 'history' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none', transition: 'all 0.2s', minHeight: '44px', touchAction: 'manipulation' }}
                    >
                        <i className="fas fa-history" style={{ marginRight: '6px' }} />History
                    </button>
                </div>
            </div>

            {selectedOrder && (
                <InvoicePreview orderId={selectedOrder} token={token} onClose={() => setSelectedOrder(null)} />
            )}

            {activeTab === 'billing' ? (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 380px', gap: '16px', flex: isMobile ? 'unset' : 1, minHeight: 0 }}>
                    
                    {/* Left: Menu & Filters */}
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '20px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', overflow: isMobile ? 'visible' : 'hidden', height: isMobile ? 'auto' : '100%', maxHeight: isMobile ? 'none' : '100%', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                            <div style={{ position: 'relative', flex: 1 }}>
                                <i className="fas fa-search" style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.9em' }} />
                                <input 
                                    type="text" 
                                    placeholder="Search food items..." 
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    style={{ width: '100%', padding: '12px 12px 12px 40px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.9em', outline: 'none' }}
                                />
                            </div>
                            <select 
                                value={activeCategory} 
                                onChange={e => setActiveCategory(e.target.value)}
                                style={{ padding: '10px 15px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.9em', fontWeight: '600', color: '#475569', cursor: 'pointer', outline: 'none' }}
                            >
                                <option value="All">All Categories</option>
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(110px, 1fr))' : 'repeat(auto-fill, minmax(130px, 1fr))', gap: isMobile ? '10px' : '15px', overflowY: isMobile ? 'visible' : 'auto', paddingRight: isMobile ? 0 : '10px', flex: isMobile ? 'unset' : 1, minHeight: isMobile ? 'auto' : 0, maxHeight: isMobile ? '360px' : 'none', overflowX: 'hidden' }}>
                            {filteredItems.map(item => {
                                const price = item.sizes?.[0]?.price || 0;
                                return (
                                    <div 
                                        key={item.item_id} 
                                        onClick={() => addToCart(item)}
                                        style={{ 
                                            padding: '15px 12px', 
                                            border: '1px solid #f1f5f9', 
                                            borderRadius: '16px', 
                                            cursor: 'pointer', 
                                            textAlign: 'center',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            background: '#fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                                            minHeight: '180px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center'
                                        }}
                                        className="menu-item-card"
                                        onMouseEnter={e => {
                                            e.currentTarget.style.borderColor = 'var(--primary-orange)';
                                            e.currentTarget.style.transform = 'translateY(-3px)';
                                            e.currentTarget.style.boxShadow = '0 8px 15px rgba(0,0,0,0.05)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.borderColor = '#f1f5f9';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.02)';
                                        }}
                                    >
                                        <div style={{ width: '100%', height: '80px', background: '#f8fafc', borderRadius: '12px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                                            {item.image_url ? (
                                                <img 
                                                    src={item.image_url} 
                                                    alt={item.name} 
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                                                />
                                            ) : (
                                                <i className="fas fa-hotdog" style={{ color: '#cbd5e1', fontSize: '1.4rem' }} />
                                            )}
                                            <i className="fas fa-hotdog" style={{ color: '#cbd5e1', fontSize: '1.4rem', display: 'none' }} />
                                        </div>
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%' }}>
                                            <span style={{ fontWeight: '700', display: 'block', fontSize: '0.85em', color: '#1e293b', lineHeight: '1.4', marginBottom: '5px' }}>{item.name}</span>
                                            <span style={{ color: 'var(--primary-orange)', fontWeight: '800', fontSize: '1.1em', display: 'block' }}>{config.currency_symbol}{price}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right: Cart & Checkout */}
                    <div style={{ background: '#fff', borderRadius: '20px', padding: '0', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%', maxHeight: isMobile ? 'none' : '100%', overflow: isMobile ? 'visible' : 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
                        
                        <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '1.2em', color: '#1e293b' }}><i className="fas fa-shopping-cart" style={{ marginRight: '8px', color: 'var(--primary-orange)' }} />Order</h3>
                                <button onClick={() => setCart([])} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '0.8em', cursor: 'pointer', fontWeight: '600' }}><i className="fas fa-trash-alt" style={{ marginRight: '6px' }} /> Clear</button>
                            </div>
                            
                            {/* Order Type Toggle */}
                            <div style={{ display: 'flex', gap: '5px', background: '#f8fafc', padding: '4px', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                                {['Dine-in', 'Takeaway', 'Delivery'].map(t => (
                                    <button 
                                        key={t}
                                        onClick={() => setOrderType(t)}
                                        style={{ 
                                            flex: 1, 
                                            padding: '8px', 
                                            borderRadius: '10px', 
                                            border: 'none', 
                                            fontSize: '0.8em',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            background: orderType === t ? '#fff' : 'transparent',
                                            color: orderType === t ? 'var(--primary-orange)' : '#64748b',
                                            boxShadow: orderType === t ? '0 2px 4px rgba(0,0,0,0.08)' : 'none',
                                            transition: 'all 0.2s'
                                        }}
                                    >{t}</button>
                                ))}
                            </div>
                        </div>

                        {/* Collapsible Info Sections */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
                            
                            {/* Customer Info Accordion */}
                            <div style={{ margin: '15px 0' }}>
                                <button 
                                    onClick={() => setShowCustInfo(!showCustInfo)}
                                    style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '12px', cursor: 'pointer' }}
                                >
                                    <span style={{ fontSize: '0.85em', fontWeight: '700', color: '#475569' }}><i className="fas fa-user" style={{ marginRight: '8px' }} /> Customer Info</span>
                                    <i className={`fas fa-chevron-${showCustInfo ? 'up' : 'down'}`} style={{ fontSize: '0.8em', color: '#94a3b8' }} />
                                </button>
                                {showCustInfo && (
                                    <div style={{ padding: '15px 10px', background: '#fff', border: '1px solid #f1f5f9', borderTop: 'none', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', fontWeight: '700', color: '#64748b' }}>NAME</label>
                                                        <input type="text" placeholder="Guest..." value={custName} onChange={e => setCustName(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9em' }} />
                                                    </div>
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', fontWeight: '700', color: '#64748b' }}>PHONE</label>
                                                        <input type="text" placeholder="Mobile..." value={custPhone} onChange={e => setCustPhone(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9em' }} />
                                                    </div>
                                                </div>
                                                {orderType === 'Dine-in' && (
                                                    <div style={{ marginTop: '10px' }}>
                                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', fontWeight: '700', color: '#64748b' }}>TABLE NUMBER</label>
                                                        <input type="text" placeholder="Table 5..." value={tableNo} onChange={e => setTableNo(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9em' }} />
                                                    </div>
                                                )}
                                                {orderType === 'Delivery' && (
                                                    <div>
                                                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.75em', fontWeight: '700', color: '#64748b' }}>ADDRESS</label>
                                                        <textarea placeholder="Delivery address..." value={custAddr} onChange={e => setCustAddr(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', minHeight: '50px', fontSize: '0.85em' }} />
                                                    </div>
                                                )}
                                    </div>
                                )}
                            </div>

                            {/* Cart Items List */}
                            <div style={{ margin: '10px 0' }}>
                                {cart.map((item, idx) => (
                                    <div key={item.item_id + '-' + idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', alignItems: 'center', background: '#fff', borderRadius: '12px' }}>
                                        <div style={{ flex: 1 }}>
                                            <span style={{ display: 'block', fontSize: '0.9em', fontWeight: '600', color: '#1e293b' }}>{item.name}</span>
                                            <span style={{ fontSize: '0.8em', color: '#64748b', fontWeight: '500' }}>{config.currency_symbol}{item.price} × {item.quantity}</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#f8fafc', padding: '4px', borderRadius: '8px', border: '1px solid #f1f5f9' }}>
                                            <button onClick={() => updateQty(item.item_id, item.name, -1)} style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: '#fff', cursor: 'pointer', color: '#64748b', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>-</button>
                                            <span style={{ fontWeight: '700', minWidth: '20px', textAlign: 'center', fontSize: '0.9em' }}>{item.quantity}</span>
                                            <button onClick={() => updateQty(item.item_id, item.name, 1)} style={{ width: '24px', height: '24px', borderRadius: '6px', border: 'none', background: '#fff', cursor: 'pointer', color: '#64748b', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>+</button>
                                        </div>
                                    </div>
                                ))}
                                {cart.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8' }}>
                                        <i className="fas fa-cart-plus" style={{ fontSize: '2rem', marginBottom: '10px', display: 'block', opacity: 0.3 }} />
                                        Cart is empty
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer Section */}
                        <div style={{ padding: '20px', background: '#fff', borderTop: '1px solid #f1f5f9' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                                <button 
                                    onClick={() => setShowDiscount(!showDiscount)}
                                    style={{ background: 'none', border: 'none', color: 'var(--primary-orange)', fontSize: '0.85em', fontWeight: '700', cursor: 'pointer', padding: 0 }}
                                >
                                    <i className="fas fa-tag" style={{ marginRight: '5px' }} /> 
                                    {appliedCoupon ? `Applied: ${couponCode}` : (discountVal > 0 ? 'Manual Discount Applied' : 'Add Discount/Coupon')}
                                </button>
                                {discountAmount > 0 && <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '0.9em' }}>-{config?.currency_symbol || '₹'}{discountAmount.toFixed(2)}</span>}
                            </div>

                            {showDiscount && (
                                <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '12px', marginBottom: '15px', marginTop: '10px', border: '1px solid #f1f5f9' }}>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input type="text" placeholder="CODE..." value={couponCode} onChange={e => setCouponCode(e.target.value)} style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.8em' }} />
                                        <button onClick={handleApplyCoupon} style={{ padding: '8px 15px', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.8em' }}>Apply</button>
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                        <input 
                                            type="text" 
                                            placeholder="Manual Disc (e.g. 50 or 10%)"
                                            onChange={e => {
                                                const v = e.target.value;
                                                if (v) {
                                                    if (v.includes('%')) { setDiscountType('Percent'); setDiscountVal(parseFloat(v) || 0); }
                                                    else { setDiscountType('Fixed'); setDiscountVal(parseFloat(v) || 0); }
                                                } else {
                                                    setDiscountVal(0);
                                                }
                                            }}
                                            style={{ flex: 1, padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.8em' }}
                                        />
                                    </div>
                                    {(appliedCoupon || discountVal > 0) && (
                                        <button onClick={() => { setAppliedCoupon(null); setDiscountVal(0); setCouponCode(''); }} style={{ width: '100%', marginTop: '8px', border: 'none', background: 'none', color: '#ef4444', fontSize: '0.75em', fontWeight: '700', cursor: 'pointer' }}>Remove Discount</button>
                                    )}
                                </div>
                            )}

                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', marginBottom: '15px', marginTop: '10px', border: '1px solid #f1f5f9' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85em', color: '#64748b' }}>
                                    <span>Subtotal</span>
                                    <span>{config.currency_symbol}{taxableSubtotal.toFixed(2)}</span>
                                </div>
                                {gstAmount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.85em', color: '#64748b' }}>
                                        <span>GST ({config.gst_percent}%)</span>
                                        <span>{config.currency_symbol}{gstAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {parseFloat(config.service_charge) > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '0.85em', color: '#64748b' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={applyServiceCharge}
                                                onChange={(e) => setApplyServiceCharge(e.target.checked)}
                                                style={{ accentColor: 'var(--primary-orange)' }}
                                            />
                                            Service Charge ({config.service_charge}%)
                                        </label>
                                        <span>{config.currency_symbol}{serviceChargeAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                {(orderType === 'Delivery' && String(config.is_delivery_enabled) === '1') && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '0.85em', color: '#64748b' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={applyDeliveryFee}
                                                onChange={(e) => setApplyDeliveryFee(e.target.checked)}
                                                style={{ accentColor: 'var(--primary-orange)' }}
                                            />
                                            Delivery Fee
                                        </label>
                                        <span>{deliveryFee > 0 ? `${config.currency_symbol}${deliveryFee.toFixed(2)}` : 'Free'}</span>
                                    </div>
                                )}
                                <div style={{ height: '1px', background: '#e2e8f0', margin: '10px 0' }} />
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ color: '#1e293b', fontWeight: '700', fontSize: '0.9em' }}>Payable Amount</span>
                                    <span style={{ color: '#1e293b', fontWeight: '800', fontSize: '1.4em' }}>{config.currency_symbol}{grandTotal.toFixed(2)}</span>
                                </div>
                            </div>

                            {/* Payment Modes */}
                            <div style={{ display: 'flex', gap: '5px', marginBottom: '15px' }}>
                                {['Cash', 'Card', 'UPI'].map(m => (
                                    <button 
                                        key={m} 
                                        onClick={() => setPaymentMode(m)}
                                        style={{ 
                                            flex: 1, 
                                            padding: '10px 5px', 
                                            borderRadius: '10px', 
                                            border: '1px solid', 
                                            borderColor: paymentMode === m ? 'var(--primary-orange)' : '#f1f5f9',
                                            background: paymentMode === m ? '#fff' : '#f8fafc',
                                            color: paymentMode === m ? 'var(--primary-orange)' : '#64748b',
                                            fontSize: '0.75em',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <i className={`fas ${m==='Cash'?'fa-money-bill-wave':m==='Card'?'fa-credit-card':'fa-mobile-alt'}`} style={{ marginRight: '6px' }} />
                                        {m}
                                    </button>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button 
                                    onClick={() => handleCheckout('Paid')}
                                    disabled={saving || cart.length === 0}
                                    style={{ flex: 2, padding: '14px', borderRadius: '15px', border: 'none', background: 'var(--primary-orange)', color: '#fff', fontWeight: '800', fontSize: '1em', cursor: 'pointer', boxShadow: '0 4px 12px rgba(255, 102, 0, 0.2)', opacity: (saving || cart.length === 0) ? 0.7 : 1 }}
                                >
                                    {saving ? 'Processing...' : 'SETTLE & PRINT'}
                                </button>
                                <button 
                                    onClick={() => handleCheckout('Pending')}
                                    disabled={saving || cart.length === 0}
                                    style={{ flex: 1, padding: '14px', borderRadius: '15px', border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: '700', fontSize: '0.85em', cursor: 'pointer' }}
                                >
                                    PENDING
                                </button>
                            </div>
                            
                            {msg.text && (
                                <div style={{ marginTop: '10px', padding: '10px', borderRadius: '10px', fontSize: '0.85em', textAlign: 'center', background: msg.type === 'success' ? '#dcfce7' : '#fee2e2', color: msg.type === 'success' ? '#166534' : '#991b1b', fontWeight: '600' }}>
                                    {msg.text}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ background: '#fff', borderRadius: '24px', padding: '25px', border: '1px solid #e2e8f0', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
                        <div>
                            <h3 style={{ margin: 0, fontSize: '1.3em', color: '#1e293b' }}>Order History</h3>
                            <p style={{ margin: 0, fontSize: '0.85em', color: '#64748b' }}>Manage and settle your recent invoices</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <select 
                                value={hFilters.status} 
                                onChange={e => setHFilters({...hFilters, status: e.target.value})}
                                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.85em', fontWeight: '600', color: '#475569', cursor: 'pointer' }}
                            >
                                <option value="">All Status</option>
                                <option value="Paid">Paid</option>
                                <option value="Advance Paid">Advance Paid</option>
                                <option value="Pending">Pending</option>
                            </select>
                            <select 
                                value={hFilters.order_type} 
                                onChange={e => setHFilters({...hFilters, order_type: e.target.value})}
                                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.85em', fontWeight: '600', color: '#475569', cursor: 'pointer' }}
                            >
                                <option value="">All Types</option>
                                <option value="Dine-in">Dine-in</option>
                                <option value="Takeaway">Takeaway</option>
                                <option value="Delivery">Delivery</option>
                            </select>
                            <div style={{ position: 'relative' }}>
                                <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8em' }} />
                                <input 
                                    type="text"
                                    placeholder="Search order..."
                                    value={hFilters.search}
                                    onChange={e => setHFilters({...hFilters, search: e.target.value})}
                                    style={{ padding: '8px 12px 8px 32px', borderRadius: '10px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '0.85em', width: '200px', outline: 'none' }}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {isMobile ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                {history.map(ord => (
                                    <div key={ord.id} style={{ background: '#fff', borderRadius: '12px', padding: '15px', border: '1px solid #e2e8f0', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                            <div>
                                                <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '1rem' }}>{ord.order_no}</div>
                                                <div style={{ fontSize: '0.8em', color: '#94a3b8' }}><i className="far fa-clock" /> {new Date(ord.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                                                <span style={{ fontSize: '0.65em', padding: '4px 8px', borderRadius: '50px', background: ord.order_status === 'Paid' ? '#dcfce7' : (ord.order_status === 'Advance Paid' ? '#dbeafe' : (ord.order_status === 'Awaiting Payment' ? '#fee2e2' : '#fef3c7')), color: ord.order_status === 'Paid' ? '#166534' : (ord.order_status === 'Advance Paid' ? '#1e40af' : (ord.order_status === 'Awaiting Payment' ? '#991b1b' : '#b45309')), fontWeight: '800', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{ord.order_status}</span>
                                                {ord.payment_txn_id && ord.order_status === 'Awaiting Payment' && (
                                                    <span style={{ fontSize: '0.6em', padding: '3px 6px', borderRadius: '50px', background: '#e0f2fe', color: '#0369a1', fontWeight: 'bold' }}>Payment Claimed (UPI: {ord.payment_txn_id})</span>
                                                )}
                                                {ord.order_status === 'Advance Paid' && ord.advance_paid && (
                                                    <span style={{ fontSize: '0.6em', padding: '3px 6px', borderRadius: '50px', background: '#fef3c7', color: '#b45309', fontWeight: 'bold' }}>Bal: {config?.currency_symbol || '₹'}{(parseFloat(ord.grand_total) - parseFloat(ord.advance_paid)).toFixed(2)}</span>
                                                )}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '0.9em' }}>
                                            <div>
                                                <div style={{ fontWeight: '600', color: '#475569' }}>{ord.table_no || ord.customer_name || 'Walking Customer'}</div>
                                                <div style={{ color: '#94a3b8' }}>{ord.order_type}</div>
                                            </div>
                                            <div style={{ fontWeight: '800', color: 'var(--primary-orange)', fontSize: '1.1em' }}>
                                                {config?.currency_symbol || '₹'}{ord.grand_total}
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: '8px', marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #e2e8f0' }}>
                                            {(ord.order_status === 'Pending' || ord.order_status === 'Awaiting Payment' || ord.order_status === 'Advance Paid') && (
                                                <button onClick={async () => {
                                                    if (isFreeTier) {
                                                        await customAlert("Bill Settlement is a Pro feature.", "Pro Feature", "info");
                                                        return;
                                                    }
                                                    setSettleOrder(ord);
                                                }} style={{ background: 'var(--primary-orange)', border: 'none', color: '#fff', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85em' }}>
                                                    {isFreeTier && <i className="fas fa-lock" />} {ord.order_status === 'Awaiting Payment' ? 'Settle & KOT' : 'Settle'}
                                                </button>
                                            )}
                                            {ord.customer_phone && (
                                                <a href={`https://wa.me/${ord.customer_phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ background: '#25D366', color: '#fff', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85em', textDecoration: 'none', textAlign: 'center' }}>
                                                    <i className="fab fa-whatsapp" /> Chat
                                                </a>
                                            )}
                                            <button onClick={async () => {
                                                if (isFreeTier) {
                                                    await customAlert("Print Invoice is a Pro feature.", "Pro Feature", "info");
                                                    return;
                                                }
                                                setSelectedOrder(ord.id);
                                            }} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', padding: '8px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85em' }}>
                                                {isFreeTier && <i className="fas fa-lock" />} <i className="fas fa-print" /> Print
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px', textAlign: 'left' }}>
                                <thead>
                                    <tr>
                                        <th style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Invoice</th>
                                        <th style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer/Table</th>
                                        <th style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</th>
                                        <th style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                                        <th style={{ padding: '12px 15px', color: '#64748b', fontSize: '0.75em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(ord => (
                                        <tr key={ord.id} style={{ background: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '15px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', borderLeft: '1px solid #f1f5f9', borderTopLeftRadius: '12px', borderBottomLeftRadius: '12px' }}>
                                                <div style={{ fontWeight: '700', color: '#1e293b', fontSize: '0.95em' }}>{ord.order_no}</div>
                                                <div style={{ fontSize: '0.75em', color: '#94a3b8', marginTop: '2px' }}><i className="far fa-clock" /> {new Date(ord.created_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                                            </td>
                                            <td style={{ padding: '15px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                                <div style={{ fontWeight: '600', color: '#475569', fontSize: '0.9em' }}>{ord.table_no || ord.customer_name || 'Walking Customer'}</div>
                                                <div style={{ fontSize: '0.75em', color: '#94a3b8', marginTop: '2px' }}>{ord.order_type}</div>
                                            </td>
                                            <td style={{ padding: '15px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                                <div style={{ fontWeight: '800', color: 'var(--primary-orange)', fontSize: '1em' }}>{config?.currency_symbol || '₹'}{ord.grand_total}</div>
                                            </td>
                                            <td style={{ padding: '15px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px' }}>
                                                    <span style={{ fontSize: '0.7em', padding: '5px 12px', borderRadius: '50px', background: ord.order_status === 'Paid' ? '#dcfce7' : (ord.order_status === 'Advance Paid' ? '#dbeafe' : (ord.order_status === 'Awaiting Payment' ? '#fee2e2' : '#fef3c7')), color: ord.order_status === 'Paid' ? '#166534' : (ord.order_status === 'Advance Paid' ? '#1e40af' : (ord.order_status === 'Awaiting Payment' ? '#991b1b' : '#b45309')), fontWeight: '800', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{ord.order_status}</span>
                                                    {ord.payment_txn_id && ord.order_status === 'Awaiting Payment' && (
                                                        <span style={{ fontSize: '0.65em', padding: '4px 8px', borderRadius: '50px', background: '#e0f2fe', color: '#0369a1', fontWeight: 'bold' }}>Claimed (UPI: {ord.payment_txn_id})</span>
                                                    )}
                                                    {ord.order_status === 'Advance Paid' && ord.advance_paid && (
                                                        <span style={{ fontSize: '0.65em', padding: '4px 8px', borderRadius: '50px', background: '#fef3c7', color: '#b45309', fontWeight: 'bold' }}>Bal: {config?.currency_symbol || '₹'}{(parseFloat(ord.grand_total) - parseFloat(ord.advance_paid)).toFixed(2)}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ padding: '15px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', borderRight: '1px solid #f1f5f9', borderTopRightRadius: '12px', borderBottomRightRadius: '12px', textAlign: 'right' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                    {(ord.order_status === 'Pending' || ord.order_status === 'Awaiting Payment' || ord.order_status === 'Advance Paid') && (
                                                        <button 
                                                            onClick={async () => {
                                                                if (isFreeTier) {
                                                                    await customAlert("Bill Settlement is a Pro feature. Please upgrade to manage payments.", "Pro Feature", "info");
                                                                    return;
                                                                }
                                                                setSettleOrder(ord);
                                                            }} 
                                                            style={{ background: 'var(--primary-orange)', border: 'none', color: '#fff', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em', boxShadow: '0 4px 6px -1px rgba(255,102,0,0.2)', opacity: isFreeTier ? 0.6 : 1 }}
                                                            title={ord.order_status === 'Awaiting Payment' ? "Settle Payment and Send to Kitchen" : "Settle Bill"}
                                                        >
                                                            {isFreeTier && <i className="fas fa-lock" style={{ marginRight: '5px' }} />} {ord.order_status === 'Awaiting Payment' ? 'Settle & KOT' : 'Settle'}
                                                        </button>
                                                    )}
                                                    {ord.customer_phone && (
                                                        <a 
                                                            href={`https://wa.me/${ord.customer_phone.replace(/\D/g, '')}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            style={{ background: '#25D366', color: '#fff', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em', textDecoration: 'none', display: 'flex', alignItems: 'center' }}
                                                        >
                                                            <i className="fab fa-whatsapp" style={{ marginRight: '5px' }} /> Chat
                                                        </a>
                                                    )}
                                                    <button 
                                                        onClick={async () => {
                                                            if (isFreeTier) {
                                                                await customAlert("Print Invoice is a Pro feature. Please upgrade to print digital receipts.", "Pro Feature", "info");
                                                                return;
                                                            }
                                                            setSelectedOrder(ord.id);
                                                        }} 
                                                        style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em', transition: '0.2s', opacity: isFreeTier ? 0.6 : 1 }}
                                                    >
                                                        {isFreeTier && <i className="fas fa-lock" style={{ marginRight: '5px' }} />} <i className="fas fa-print" /> Print
                                                    </button>
                                                    
                                                    <button 
                                                        onClick={() => handleDeleteOrder(ord.id)}
                                                        disabled={saving}
                                                        style={{ background: '#fee2e2', border: 'none', color: '#ef4444', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.8em', transition: '0.2s' }}
                                                        title="Permanently Delete Order"
                                                    >
                                                        <i className="fas fa-trash-alt" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {history.length === 0 && (
                            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
                                <i className="fas fa-file-invoice" style={{ fontSize: '3rem', marginBottom: '15px', opacity: 0.2, display: 'block' }} />
                                No results found for your search.
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Settlement Modal */}
            {settleOrder && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
                    <div style={{ background: '#fff', padding: '25px', borderRadius: '16px', width: '100%', maxWidth: '380px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0 }}>Settle Payment</h3>
                        <p style={{ color: '#64748b', fontSize: '0.9em', marginBottom: '20px' }}>
                            Collect payment for <b>{settleOrder.order_no}</b><br/>
                            Total Due: <b style={{ fontSize: '1.2em', color: '#000' }}>{config?.currency_symbol || '₹'}{settleOrder.grand_total}</b>
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                            {['Cash', 'Card', 'UPI'].map(mode => (
                                <button 
                                    key={mode}
                                    disabled={saving}
                                    onClick={() => handleSettleOrder(settleOrder.id, mode)}
                                    style={{ padding: '15px 5px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#f8fafc', cursor: 'pointer', fontWeight: '700' }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-orange)'}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                >
                                    {mode}
                                </button>
                            ))}
                        </div>
                        <button 
                            onClick={() => setSettleOrder(null)}
                            style={{ width: '100%', padding: '10px', border: 'none', background: 'none', color: '#64748b', fontWeight: '600', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Variant Selection Modal */}
            {variantModalItem && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
                    <div style={{ background: '#fff', padding: '30px', borderRadius: '16px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '10px' }}>Select Variant</h3>
                        <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '0.9em' }}>{variantModalItem.name}</p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {variantModalItem.sizes.map(sz => (
                                <button 
                                    key={sz.size_name}
                                    onClick={() => addToCart(variantModalItem, sz)}
                                    style={{ 
                                        padding: '15px', 
                                        borderRadius: '12px', 
                                        border: '1px solid #e2e8f0', 
                                        background: '#f8fafc', 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        cursor: 'pointer',
                                        fontWeight: '600'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary-orange)'}
                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                                >
                                    <span>{sz.size_name}</span>
                                    <span style={{ color: 'var(--primary-orange)' }}>{config.currency_symbol}{sz.price}</span>
                                </button>
                            ))}
                        </div>

                        <button 
                            onClick={() => setVariantModalItem(null)}
                            style={{ width: '100%', marginTop: '20px', padding: '12px', background: 'none', border: 'none', color: '#64748b', fontWeight: '600', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default POS;
